import React from "react";
import { useScrollAnimation } from "../hooks/useScrollAnimation";

const services = [
  {
    icon: "🌐",
    title: "Website Development",
    desc: "Modern, fast, SEO-optimized websites built to convert visitors into clients.",
    bg: "from-blue-50 to-sky-100",
    border: "border-blue-200 hover:border-blue-400",
    iconBg: "bg-blue-100",
    tag: "Most Popular",
    tagColor: "bg-blue-600 text-white",
  },
  {
    icon: "📱",
    title: "App Development",
    desc: "iOS & Android apps with seamless UX, built for performance and scale.",
    bg: "from-indigo-50 to-purple-50",
    border: "border-indigo-200 hover:border-indigo-400",
    iconBg: "bg-indigo-100",
    tag: null,
    tagColor: "",
  },
   {
    icon: "⚙️",
    title: "Software Development",
    desc: "Custom software, dashboards & SaaS tools tailored to your business.",
    bg: "from-teal-50 to-green-50",
    border: "border-teal-200 hover:border-teal-400",
    iconBg: "bg-teal-100",
    tag: null,
    tagColor: "",
  },
  {
    icon: "🤖",
    title: "AI Integration",
    desc: "Automate workflows, add AI chatbots, and future-proof your business.",
    bg: "from-sky-50 to-blue-100",
    border: "border-sky-200 hover:border-sky-400",
    iconBg: "bg-sky-100",
    tag: "New",
    tagColor: "bg-sky-600 text-white",
  },
  {
    icon: "🎬",
    title: "Video Editing",
    desc: "Cinematic edits, reels, and brand videos that stop the scroll.",
    bg: "from-orange-50 to-amber-50",
    border: "border-orange-200 hover:border-orange-400",
    iconBg: "bg-orange-100",
    tag: null,
    tagColor: "",
  },
  {
    icon: "📸",
    title: "UGC Content",
    desc: "Authentic user-generated style content that builds trust and drives sales.",
    bg: "from-pink-50 to-rose-50",
    border: "border-pink-200 hover:border-pink-400",
    iconBg: "bg-pink-100",
    tag: "Trending",
    tagColor: "bg-pink-500 text-white",
  },
  {
    icon: "🎨",
    title: "Graphic Design",
    desc: "Logos, branding, social posts — visuals that make your brand unforgettable.",
    bg: "from-yellow-50 to-amber-50",
    border: "border-yellow-200 hover:border-yellow-400",
    iconBg: "bg-yellow-100",
    tag: null,
    tagColor: "",
  },
  {
    icon: "📣",
    title: "Meta Ads",
    desc: "High-converting Facebook & Instagram ad campaigns built for ROI.",
    bg: "from-blue-50 to-indigo-50",
    border: "border-blue-200 hover:border-indigo-400",
    iconBg: "bg-blue-100",
    tag: null,
    tagColor: "",
  },
 
];

export default function Services() {
  const ref = useScrollAnimation();

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Fraunces:wght@700;900&display=swap');
        .svc-heading { font-family: 'Fraunces', serif; }
        .svc-body { font-family: 'Plus Jakarta Sans', sans-serif; }
      `}</style>

      <section
        id="services"
        className="pt-2 pb-16 relative bg-gradient-to-b from-white to-blue-50 overflow-hidden"
      >
        {/* Decorative blobs */}
        <div className="absolute top-0 right-0 w-72 h-72 bg-blue-100/50 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-sky-100/40 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-5 md:px-10 relative">

          {/* Heading */}
          <div className="text-center mb-10">
  <span className="trust-body inline-block text-sm font-bold text-blue-600 tracking-widest uppercase">
              -: What We Do :-
            </span>
            </div>
            <div className="text-center mb-10">
            <h2
              className="svc-heading font-black text-4xl md:text-6xl text-black mb-4 leading-tight"
            >
              Services That{" "}
              <span
                style={{
                  background: "linear-gradient(135deg, #1d4ed8 0%, #0ea5e9 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Deliver
              </span>
            </h2>
            <p className="svc-body text-slate-500 text-lg max-w-xl mx-auto font-medium">
              One team. Every digital need. No outsourcing, no compromises.
            </p>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {services.map((s, i) => (
              <div
                key={i}
                className={`relative group bg-gradient-to-br ${s.bg} border ${s.border} rounded p-6 overflow-hidden shadow-sm hover:shadow-lg hover:shadow-blue-100 transition-all duration-300 hover:-translate-y-1`}
              >
                {/* Tag badge */}
                {s.tag && (
                  <span className={`svc-body absolute top-4 right-4 text-[10px] font-bold px-2.5 py-0.5 rounded-full ${s.tagColor}`}>
                    {s.tag}
                  </span>
                )}

                {/* Icon */}
                <div className={`w-13 h-13 w-14 h-14 rounded ${s.iconBg} flex items-center justify-center text-3xl mb-5 shadow-sm group-hover:scale-110 transition-transform duration-300`}>
                  {s.icon}
                </div>

                {/* Title */}
                <h3 className="svc-heading font-bold text-lg text-black mb-2 leading-snug">
                  {s.title}
                </h3>

                {/* Desc */}
                <p className="svc-body text-slate-500 text-sm leading-relaxed mb-5 font-medium">
                  {s.desc}
                </p>

                {/* CTA link */}
                <a
                  href="#portfolio"
                  className="svc-body inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:text-blue-800 transition-colors duration-200 group/link"
                >
                  View Work
                  <span className="group-hover/link:translate-x-1 inline-block transition-transform duration-200">→</span>
                </a>

                {/* Bottom accent line on hover */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-400 to-sky-400 scale-x-0 group-hover:scale-x-100 transition-transform duration-400 rounded origin-left" />
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-14">
            <p className="svc-body text-slate-400 text-sm mb-4 font-medium">
              Not sure what you need? Let's talk.
            </p>
            <a
              href="#contact"
              className="svc-body inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm px-8 py-4 rounded transition-all duration-300 hover:scale-105 shadow-lg shadow-blue-200"
            >
              Get Free Consultation →
            </a>
          </div>
        </div>
      </section>
    </>
  );
}